import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { WebView } from 'react-native-webview';

const MAX_PINS = 50;

function dedupePins(pins) {
  if (!Array.isArray(pins)) return [];
  const seen = new Set();
  const out = [];
  for (const p of pins) {
    const lat = Number(p.lat);
    const lon = Number(p.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const key = `${lat.toFixed(4)}|${lon.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      lat,
      lon,
      label: p.label || p.student_name || '',
    });
    if (out.length >= MAX_PINS) break;
  }
  return out;
}

function buildLeafletMapHtml(pins) {
  const subset = dedupePins(pins);
  const pinsJson = JSON.stringify(subset).replace(/</g, '\\u003c');
  const defaultCenter =
    subset.length > 0
      ? [
          subset.reduce((a, p) => a + p.lat, 0) / subset.length,
          subset.reduce((a, p) => a + p.lon, 0) / subset.length,
        ]
      : [20, 0];

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" crossorigin="" />
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" crossorigin=""></script>
  <style>
    html, body, #map { height: 100%; width: 100%; margin: 0; padding: 0; background: #e8e8e8; }
    .leaflet-control-attribution { font-size: 10px !important; }
  </style>
</head>
<body>
  <div id="map"></div>
  <script>
    (function () {
      var pins = ${pinsJson};
      var map = L.map('map', { zoomControl: true, attributionControl: true });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(map);
      if (!pins.length) {
        map.setView([${defaultCenter[0]}, ${defaultCenter[1]}], 2);
        return;
      }
      var markers = [];
      pins.forEach(function (p) {
        var m = L.marker([p.lat, p.lon]);
        if (p.label) m.bindPopup(String(p.label).replace(/</g, ''));
        m.addTo(map);
        markers.push(m);
      });
      if (pins.length === 1) {
        map.setView([pins[0].lat, pins[0].lon], 8);
      } else {
        var group = L.featureGroup(markers);
        map.fitBounds(group.getBounds().pad(0.15), { maxZoom: 10 });
      }
    })();
  </script>
</body>
</html>`;
}

/**
 * Login geo map (Leaflet + OSM). Web uses iframe (WebView is not supported on RN Web).
 */
export default function LoginMapPanel({ pins, height = 280, isDark = false }) {
  const mapPins = useMemo(() => dedupePins(pins), [pins]);
  const html = useMemo(() => buildLeafletMapHtml(mapPins), [mapPins]);

  if (!mapPins.length) {
    return null;
  }

  const wrapStyle = [styles.wrap, { height }, isDark && styles.wrapDark];

  if (Platform.OS === 'web') {
    return (
      <View style={wrapStyle} dataSet={{ loginMapPanel: 'true' }}>
        {React.createElement('iframe', {
          title: 'Login locations map',
          srcDoc: html,
          sandbox: 'allow-scripts allow-same-origin',
          style: {
            display: 'block',
            width: '100%',
            height,
            border: 'none',
            borderRadius: 4,
            backgroundColor: isDark ? '#2a2a2a' : '#e5e5e5',
          },
        })}
      </View>
    );
  }

  return (
    <View style={wrapStyle}>
      <WebView
        originWhitelist={['*']}
        source={{ html }}
        style={[styles.webview, { height }]}
        scrollEnabled={false}
        nestedScrollEnabled
        javaScriptEnabled
        domStorageEnabled
        allowsInlineMediaPlayback
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#e5e5e5',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#dddbda',
  },
  wrapDark: {
    backgroundColor: '#2a2a2a',
    borderColor: '#3e3e3c',
  },
  webview: {
    width: '100%',
    backgroundColor: 'transparent',
  },
});
